import { LabeledInfo } from "./LabeledInfo";
import {
  AssignmentIndOutlined,
  CheckCircleOutline,
  ExpandMoreOutlined,
  Inventory2Outlined,
  SearchOutlined,
  WarningAmberOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  claimRepackagingTask,
  claimProcessTransfer,
  completeProcessTransfer,
  completeRepackagingTask,
  loadRepackagingOptions,
  loadRepackagingTask,
  loadRepackagingTasks,
  loadProcessTransfers,
  loadProcessPoints,
  reportRepackagingException,
  resolveRepackagingException,
  ProcessTransferTask,
  RepackagingTask
} from "./api";
import { PageHeader } from "./PageHeader";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";
import { SourceTrace } from "./SourceTrace";
import { ListRowActions } from "./ListRowActions";
import { compareOptionalDates, matchesListSyntax } from "./listSearch";
import { operationalStatusColor, operationalStatusLabel } from "./operationalStatus";
import { formatMeasurement } from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";

interface RepackagingPageProps {
  onBack: () => void;
  permissions: string[];
}

export function RepackagingPage({
  onBack,
  initialTaskId = null,
  initialOpenDetail = false,
  permissions
}: RepackagingPageProps & {
  initialTaskId?: string | null;
  initialOpenDetail?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "fr-FR";
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const initialTaskOpened = useRef(false);
  const [targetId, setTargetId] = useState("");
  const [comment, setComment] = useState("");
  const [departurePointId, setDeparturePointId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const [exceptionCode, setExceptionCode] = useState("PACKAGING_UNAVAILABLE");
  const [exceptionComment, setExceptionComment] = useState("");
  const [resolutionAction, setResolutionAction] = useState("RETRY");
  const [listTab, setListTab] = useState<"open" | "closed">("open");
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [syntaxSearch, setSyntaxSearch] = useState("");
  const [mobileSelectedId, setMobileSelectedId] = useState<string | null>(null);
  const canExecute = permissions.includes("repackaging_task:execute");
  const canComplete = permissions.includes("repackaging_task:complete");
  const canExecuteTransfer = permissions.includes("process_transfer:execute");
  const exceptionLabel = (code: string) => t(`repackaging.exceptions.${code}`, {
    defaultValue: code.replaceAll("_", " ")
  });

  const tasks = useQuery({
    queryKey: ["repackaging-tasks"],
    queryFn: loadRepackagingTasks
  });
  const transfers = useQuery({
    queryKey: ["process-transfers"],
    queryFn: loadProcessTransfers
  });
  const activeTransferByHu = useMemo(() => new Map(
    (transfers.data ?? [])
      .filter((transfer) => !["COMPLETED", "CANCELLED"].includes(transfer.status))
      .map((transfer) => [transfer.handlingUnitId, transfer])
  ), [transfers.data]);
  useEffect(() => {
    if (!initialTaskId || initialTaskOpened.current || !tasks.data) return;
    const task = tasks.data.find((entry) => entry.id === initialTaskId);
    if (task) {
      initialTaskOpened.current = true;
      setListTab(["COMPLETED", "CANCELLED"].includes(task.status)
        ? "closed" : "open");
      if (initialOpenDetail) setSelectedId(initialTaskId);
      window.setTimeout(() => document.getElementById(
        `repackaging-${initialTaskId}`
      )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
    }
  }, [initialOpenDetail, initialTaskId, tasks.data]);
  const detail = useQuery({
    queryKey: ["repackaging-task", selectedId],
    queryFn: () => loadRepackagingTask(selectedId!),
    enabled: Boolean(selectedId)
  });
  const options = useQuery({
    queryKey: ["repackaging-options", selectedId],
    queryFn: () => loadRepackagingOptions(selectedId!),
    enabled: Boolean(selectedId)
  });
  const departurePoints = useQuery({
    queryKey: ["process-points", detail.data?.warehouseId],
    queryFn: () => loadProcessPoints(detail.data!.warehouseId),
    enabled: Boolean(detail.data?.warehouseId)
  });
  useEffect(() => {
    setTargetId(detail.data?.targetItemPackagingId ?? "");
    setComment(detail.data?.completionComment ?? "");
    const points = departurePoints.data ?? [];
    setDeparturePointId(
      points.find((point) => point.defaultForRepacking)?.id
      ?? points.find((point) =>
        point.code === detail.data?.processPointCode
        && point.putawaySourceEnabled)?.id
      ?? ""
    );
    setError(null);
  }, [
    detail.data?.id,
    detail.data?.targetItemPackagingId,
    detail.data?.completionComment,
    detail.data?.processPointCode,
    departurePoints.data
  ]);

  const refresh = async (updated: RepackagingTask) => {
    queryClient.setQueryData(["repackaging-task", updated.id], updated);
    await queryClient.invalidateQueries({ queryKey: ["repackaging-tasks"] });
    await queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
    await queryClient.invalidateQueries({ queryKey: ["putaway-alerts"] });
  };
  const claim = useMutation({
    mutationFn: claimRepackagingTask,
    onSuccess: refresh,
    onError: (cause) => setError(cause instanceof Error ? cause.message :
      t("repackaging.errors.claim"))
  });
  const transferAction = useMutation({
    mutationFn: (task: ProcessTransferTask) => {
      return task.status === "OPEN"
        ? claimProcessTransfer(task) : completeProcessTransfer(task);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["process-transfers"] });
      await queryClient.invalidateQueries({ queryKey: ["repackaging-tasks"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-alerts"] });
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message :
      t("repackaging.errors.transfer"))
  });
  const complete = useMutation({
    mutationFn: ({ task, packagingId, value }: {
      task: RepackagingTask;
      packagingId: string;
      value: string;
    }) => completeRepackagingTask(
      task, packagingId, departurePointId, value
    ),
    onSuccess: refresh,
    onError: (cause) => setError(cause instanceof Error ? cause.message :
      t("repackaging.errors.complete"))
  });
  const reportException = useMutation({
    mutationFn: ({ task, code, value }: {
      task: RepackagingTask; code: string; value: string;
    }) => reportRepackagingException(task, code, value),
    onSuccess: async (updated) => {
      setExceptionDialogOpen(false);
      setExceptionComment("");
      await refresh(updated);
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message :
      t("repackaging.errors.report"))
  });
  const resolveException = useMutation({
    mutationFn: ({ task, action, value }: {
      task: RepackagingTask; action: string; value: string;
    }) => resolveRepackagingException(task, action, value),
    onSuccess: async (updated) => {
      setExceptionComment("");
      await refresh(updated);
      await queryClient.invalidateQueries({ queryKey: ["quality-inspections"] });
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message :
      t("repackaging.errors.resolve"))
  });
  const filteredTasks = useMemo(() =>
    [...(tasks.data ?? [])].filter((task) =>
      ["COMPLETED", "CANCELLED"].includes(task.status)
        === (listTab === "closed")
      && (!warehouseFilter || task.warehouseCode === warehouseFilter)
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${task.id} ${task.repackagingNumber} ${task.huNumber}`,
        article: task.itemCode,
        serie: task.serialNumbers,
        entrepot: task.warehouseCode,
        gare: task.processPointCode,
        raison: task.reportedComment,
        conditionnement:
          `${task.originalPackagingCode ?? ""} ${task.targetPackagingCode ?? ""}`,
        statut: task.status,
        date: `${task.createdAt} ${task.completedAt ?? ""}`
      }, syntaxSearch))
    ).sort((left, right) => compareOptionalDates(
      listTab === "closed" ? left.completedAt : left.createdAt,
      listTab === "closed" ? right.completedAt : right.createdAt,
      sortDirection
    )), [
      listTab, sortDirection, syntaxSearch, tasks.data, warehouseFilter
    ]);
  const openCount = tasks.data?.filter(
    (task) => !["COMPLETED", "CANCELLED"].includes(task.status)
  ).length ?? 0;
  const closedCount = (tasks.data?.length ?? 0) - openCount;
  const warehouseCodes = [...new Set(
    tasks.data?.map((task) => task.warehouseCode) ?? []
  )].sort();
  useEffect(() => setMobileSelectedId(null), [listTab]);
  const selectedMobileTask = filteredTasks.find((task) =>
    task.id === mobileSelectedId) ?? null;
  const selectedMobileTransfer = selectedMobileTask
    ? activeTransferByHu.get(selectedMobileTask.handlingUnitId) ?? null : null;
  const mobilePrimaryLabel = selectedMobileTask?.status === "WAITING_TRANSFER"
    ? selectedMobileTransfer?.status === "OPEN"
      ? t("repackaging.actions.claimTransfer") : t("repackaging.actions.confirmDeposit")
    : selectedMobileTask?.status === "OPEN" ? t("repackaging.actions.claim")
      : selectedMobileTask?.status === "IN_PROGRESS" ? t("repackaging.actions.continue")
        : selectedMobileTask?.status === "EXCEPTION" ? t("repackaging.actions.handleException")
          : selectedMobileTask ? t("repackaging.actions.view") : t("repackaging.actions.select");
  const mobilePrimaryAvailable = Boolean(selectedMobileTask)
    && (selectedMobileTask?.status !== "WAITING_TRANSFER"
      || Boolean(selectedMobileTransfer && canExecuteTransfer))
    && (selectedMobileTask?.status !== "OPEN" || canExecute)
    && (selectedMobileTask?.status !== "IN_PROGRESS" || canComplete)
    && (selectedMobileTask?.status !== "EXCEPTION" || canExecute);
  const executeMobilePrimary = () => {
    if (!selectedMobileTask) return;
    if (selectedMobileTask.status === "WAITING_TRANSFER"
      && selectedMobileTransfer && canExecuteTransfer) {
      transferAction.mutate(selectedMobileTransfer);
    } else if (selectedMobileTask.status === "OPEN" && canExecute) {
      claim.mutate(selectedMobileTask);
    } else {
      setSelectedId(selectedMobileTask.id);
    }
  };
  const statusTabs = [
    { value: "open" as const, label: t("repackaging.tabs.open"),
      mobileLabel: t("repackaging.tabsMobile.open"), count: openCount },
    { value: "closed" as const, label: t("repackaging.tabs.closed"),
      mobileLabel: t("repackaging.tabsMobile.closed"), count: closedCount }
  ];

  return (
    <Stack spacing={1.5} sx={{
      height: { xs: "100%", sm: "auto", md: "100%" }, minHeight: 0,
      overflow: { xs: "hidden", sm: "visible", md: "hidden" }
    }}>
      <MobileOperationalHeader moduleLabel={t("repackaging.module")}
        search={syntaxSearch} onSearchChange={setSyntaxSearch}
        searchPlaceholder={t("repackaging.searchPlaceholder")} onBack={onBack} />
      <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>
        <PageHeader title={t("repackaging.title")} compact onBack={onBack} />
      </Box>

      <ResponsiveStatusTabs value={listTab} items={statusTabs}
        onChange={setListTab} />

      <Card variant="outlined" sx={{ display: { xs: "none", sm: "block" },
        flexShrink: 0 }}>
        <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField size="small" label={t("common.filters.search")}
              placeholder={t("repackaging.filters.syntaxPlaceholder")}
              value={syntaxSearch}
              onChange={(event) => setSyntaxSearch(event.target.value)}
              helperText={t("repackaging.filters.syntaxHelp")}
              InputProps={{ startAdornment:
                <SearchOutlined color="action" sx={{ mr: 1 }} /> }}
              sx={{ flex: 2 }} />
            <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
              <InputLabel>{t("repackaging.filters.warehouse")}</InputLabel>
              <Select label={t("repackaging.filters.warehouse")} value={warehouseFilter}
                onChange={(event) => setWarehouseFilter(event.target.value)}>
                <MenuItem value="">{t("common.filters.all")}</MenuItem>
                {warehouseCodes.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t("repackaging.filters.dateOrder")}</InputLabel>
              <Select label={t("repackaging.filters.dateOrder")} value={sortDirection}
                onChange={(event) => setSortDirection(event.target.value as
                  "asc" | "desc")}>
                <MenuItem value="asc">{t("common.filters.ascending")}</MenuItem>
                <MenuItem value="desc">{t("common.filters.descending")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>
      {tasks.isLoading && <CircularProgress />}
      {tasks.isError && (
        <Alert severity="error">
          {t("repackaging.errors.load")}
        </Alert>
      )}
      {tasks.data?.length === 0 && (
        <Alert severity="info">{t("repackaging.empty.all")}</Alert>
      )}
      {tasks.data && filteredTasks.length === 0 && tasks.data.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("repackaging.empty.filtered")}
        </Alert>
      )}
      <Stack spacing={2} sx={{
        flex: { xs: "1 1 0", sm: "none", md: 1 }, minHeight: 0,
        overflowY: { xs: "auto", sm: "visible", md: "auto" },
        pr: .5, pb: { xs: 11, md: 0 }
      }}>
        {filteredTasks.map((task) => (
          <Card key={task.id} id={`repackaging-${task.id}`}
            variant="outlined" onClick={(event) => {
              if ((event.target as HTMLElement).closest(
                "button,a,[role='button']")) return;
              setMobileSelectedId(task.id);
            }} sx={{
              flexShrink: 0,
              cursor: { xs: "pointer", sm: "default" },
              outline: { xs: mobileSelectedId === task.id
                ? "2px solid" : "none", sm: "none" },
              outlineColor: "primary.main",
              borderColor: task.id === initialTaskId ? "primary.main" : undefined,
              borderWidth: task.id === initialTaskId ? 2 : undefined
            }}>
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
                alignItems={{ sm: "center" }}>
                <Inventory2Outlined color="secondary" />
                <Stack spacing={1.25} sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                    <LabeledInfo label={t("repackaging.card.reference")} prominent>{task.repackagingNumber}</LabeledInfo>
                    <LabeledInfo label={t("quality.derogationCard.hu")} prominent>{task.huNumber}</LabeledInfo>
                    <LabeledInfo label={t("quality.derogationCard.item")} prominent>{task.itemCode}</LabeledInfo>
                    <LabeledInfo label={t("quality.derogationCard.quantity")} prominent>{<> {task.quantity} {task.uom}</>}</LabeledInfo>
                    <LabeledInfo label={t("quality.derogationCard.warehouse")}>{task.warehouseCode}</LabeledInfo>
                    <LabeledInfo label={t("repackaging.card.packaging")}>{task.targetPackagingCode ?? task.originalPackagingCode ?? t("repackaging.labels.undefined")}</LabeledInfo>
                    <LabeledInfo label={t("quality.derogationCard.client")}>{task.clientName ?? task.clientCode ?? "—"}</LabeledInfo>
                    {task.asnNumber && <LabeledInfo label={t("quality.derogationCard.asn")}>{task.asnNumber}</LabeledInfo>}
                    {task.receiptNumber && <LabeledInfo label={t("quality.derogationCard.receipt")}>{task.receiptNumber}</LabeledInfo>}
                    {task.serialNumbers && <LabeledInfo label={t("quality.derogationCard.serial")}>{task.serialNumbers}</LabeledInfo>}
                  </Box>
                </Stack>
                <ListRowActions
                  actions={
                    task.status === "WAITING_TRANSFER" && canExecuteTransfer
                      && ["OPEN", "IN_PROGRESS"].includes(
                        activeTransferByHu.get(task.handlingUnitId)?.status ?? ""
                      ) ? (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        disabled={transferAction.isPending}
                        onClick={() => {
                          const transfer = activeTransferByHu.get(task.handlingUnitId);
                          if (transfer) transferAction.mutate(transfer);
                        }}>
                        {activeTransferByHu.get(task.handlingUnitId)?.status === "OPEN"
                          ? t("repackaging.actions.claimTransfer")
                          : t("repackaging.actions.confirmDeposit")}
                      </Button>
                    ) : task.status === "OPEN" && canExecute ? (
                      <Button variant="contained" startIcon={<AssignmentIndOutlined />}
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        disabled={claim.isPending}
                        onClick={() => claim.mutate(task)}>
                        {t("repackaging.actions.claim")}
                      </Button>
                    ) : task.status === "IN_PROGRESS" && canComplete ? (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        onClick={() => setSelectedId(task.id)}>
                        {t("repackaging.actions.continue")}
                      </Button>
                    ) : task.status === "EXCEPTION" && canExecute ? (
                      <Button variant="contained" color="warning"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        onClick={() => setSelectedId(task.id)}>
                        {t("repackaging.actions.handleException")}
                      </Button>
                    ) : undefined
                  }
                  status={
                    <Chip label={operationalStatusLabel(task.status)}
                      color={operationalStatusColor(task.status)} />
                  } />
                <IconButton className="mobile-card-expand"
                  aria-label={t("repackaging.labels.details")}
                  onClick={() => setExpandedIds((current) =>
                    current.includes(task.id)
                      ? current.filter((id) => id !== task.id)
                      : [...current, task.id]
                  )}>
                  <ExpandMoreOutlined sx={{ transition: "transform .2s",
                    transform: expandedIds.includes(task.id)
                      ? "rotate(180deg)" : "rotate(0deg)" }} />
                </IconButton>
              </Stack>
              <Collapse in={expandedIds.includes(task.id)} unmountOnExit>
                <Box sx={{ mt: 2, ml: { sm: 5 }, p: 2, bgcolor: "action.hover",
                  borderRadius: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography fontWeight={800}>
                        {t("repackaging.labels.requestDetails")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("repackaging.labels.reason", {
                          reason: task.reportedComment || exceptionLabel(task.reasonCode)
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("repackaging.labels.processingStation", {
                          station: task.processPointCode
                        })}
                      </Typography>
                    </Box>
                    {task.status === "WAITING_TRANSFER" && (() => {
                      const transfer = transfers.data?.find((candidate) =>
                        candidate.handlingUnitId === task.handlingUnitId
                        && candidate.transferType === "QUALITY_TO_REPACKAGING"
                        && !["COMPLETED", "CANCELLED"].includes(candidate.status));
                      return transfer ? (
                        <Alert severity="info">
                          <Typography fontWeight={800}>
                            {t("repackaging.labels.transferInProgress")}
                          </Typography>
                          <Typography variant="body2">
                            {t("repackaging.labels.transferStatus", {
                              source: transfer.sourcePointCode,
                              destination: transfer.destinationPointCode,
                              status: operationalStatusLabel(transfer.status)
                            })}
                          </Typography>
                          <Typography variant="caption">
                            {t("repackaging.labels.technicalId", { id: transfer.id })}
                          </Typography>
                        </Alert>
                      ) : (
                        <Alert severity="warning">
                          {t("repackaging.errors.missingTransfer")}
                        </Alert>
                      );
                    })()}
                    <Box>
                      <Button variant="outlined"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        onClick={() => setSelectedId(task.id)}>
                        {t("repackaging.actions.fullDetails")}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <MobileActionBar primaryLabel={mobilePrimaryLabel}
        primaryDisabled={!mobilePrimaryAvailable}
        onPrimary={executeMobilePrimary} />

      <Dialog open={Boolean(selectedId)} onClose={() => setSelectedId(null)}
        fullWidth maxWidth="md">
        <DialogTitle>
          {t("repackaging.labels.task", {
            number: detail.data?.repackagingNumber ?? ""
          })}
          {detail.data ? ` · ${detail.data.huNumber}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {detail.isLoading && <CircularProgress />}
          {detail.data && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={operationalStatusLabel(detail.data.status)}
                  color={operationalStatusColor(detail.data.status)} />
                <Chip label={`${detail.data.quantity} ${detail.data.uom}`} />
                <Chip label={detail.data.processPointCode} />
              </Stack>
              <SourceTrace sources={[
                detail.data.asnNumber, detail.data.receiptNumber,
                detail.data.huNumber, detail.data.repackagingNumber
              ]} step={t("repackaging.labels.step")} client={detail.data.clientName
                ?? detail.data.clientCode} />
              <Box>
                <Typography variant="overline">
                  {t("repackaging.labels.itemTraceability")}
                </Typography>
                <Typography>{detail.data.itemCode}</Typography>
                {detail.data.serialNumbers && (
                  <Typography>{t("repackaging.labels.serialNumber", {
                    numbers: detail.data.serialNumbers
                  })}</Typography>
                )}
              </Box>
              <Alert severity="warning">
                {t("repackaging.labels.originalPackaging", {
                  packaging: detail.data.originalPackagingCode
                    ?? t("repackaging.labels.undefined")
                })}
                {" — "}{detail.data.reportedComment}
              </Alert>
              {detail.data.assignedUserName && (
                <Typography color="text.secondary">
                  {t("repackaging.labels.assignedTo", {
                    user: detail.data.assignedUserName
                  })}
                </Typography>
              )}
              {detail.data.status === "IN_PROGRESS" && canComplete && (
                <>
                  <FormControl fullWidth required>
                    <InputLabel id="target-packaging-label">
                      {t("repackaging.labels.targetPackaging")}
                    </InputLabel>
                    <Select
                      labelId="target-packaging-label"
                      label={t("repackaging.labels.targetPackaging")}
                      data-testid="repackaging-target-packaging"
                      value={targetId}
                      onChange={(event) => setTargetId(event.target.value)}
                    >
                      {options.data?.map((option) => (
                        <MenuItem key={option.itemPackagingId}
                          value={option.itemPackagingId}>
                          {option.packagingCode} · {option.displayName}
                          {" · "}{formatMeasurement(option.lengthMm,
                            "LENGTH", measurements, locale)} ×
                          {" "}{formatMeasurement(option.widthMm,
                            "LENGTH", measurements, locale)} ×
                          {" "}{formatMeasurement(option.heightMm,
                            "LENGTH", measurements, locale)} ·
                          {" "}{formatMeasurement(option.grossWeightKg,
                            "MASS", measurements, locale)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {options.data?.length === 0 && (
                    <Alert severity="error">
                      {t("repackaging.errors.noPackaging")}
                    </Alert>
                  )}
                  <FormControl fullWidth required>
                    <InputLabel id="repacking-departure-point-label">
                      {t("repackaging.labels.departureStation")}
                    </InputLabel>
                    <Select labelId="repacking-departure-point-label"
                      label={t("repackaging.labels.departureStation")} value={departurePointId}
                      data-testid="repackaging-departure-point"
                      onChange={(event) =>
                        setDeparturePointId(event.target.value)}>
                      {departurePoints.data
                        ?.filter((point) => point.status === "ACTIVE"
                          && point.putawaySourceEnabled)
                        .map((point) => (
                          <MenuItem key={point.id} value={point.id}>
                            {point.code} · {point.displayName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label={t("repackaging.labels.repackagingComment")}
                    data-testid="repackaging-comment"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    multiline minRows={3} required
                    helperText={t("repackaging.help.requiredThree")}
                  />
                </>
              )}
              {detail.data.status === "EXCEPTION" && (
                <>
                  <Alert severity="error">
                    {exceptionLabel(detail.data.exceptionCode ?? "OTHER")} —{" "}
                    {detail.data.exceptionComment}
                    {detail.data.exceptionReportedByName
                      ? ` · ${detail.data.exceptionReportedByName}` : ""}
                  </Alert>
                  {canExecute && (
                    <>
                      <FormControl fullWidth required>
                        <InputLabel id="repack-resolution-label">
                          {t("repackaging.labels.resolutionAction")}
                        </InputLabel>
                        <Select labelId="repack-resolution-label"
                          label={t("repackaging.labels.resolutionAction")}
                          value={resolutionAction}
                          onChange={(event) =>
                            setResolutionAction(event.target.value)}>
                          <MenuItem value="RETRY">
                            {t("repackaging.resolutions.RETRY")}
                          </MenuItem>
                          <MenuItem value="SEND_TO_QUALITY">
                            {t("repackaging.resolutions.SEND_TO_QUALITY")}
                          </MenuItem>
                          <MenuItem value="CANCEL">
                            {t("repackaging.resolutions.CANCEL")}
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <TextField label={t("repackaging.labels.resolutionComment")}
                        value={exceptionComment}
                        onChange={(event) =>
                          setExceptionComment(event.target.value)}
                        multiline minRows={3} required
                        helperText={t("repackaging.help.requiredThree")} />
                    </>
                  )}
                </>
              )}
              {detail.data.status === "COMPLETED" && (
                <Alert severity="success">
                  {t("repackaging.labels.newPackaging", {
                    packaging: detail.data.targetPackagingCode
                  })}
                  {" "}{detail.data.completionComment}
                </Alert>
              )}
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedId(null)}>
            {t("common.actions.close")}
          </Button>
          {detail.data?.status === "OPEN" && canExecute && (
            <Button variant="contained"
              startIcon={<AssignmentIndOutlined />}
              data-testid="repackaging-claim"
              disabled={claim.isPending}
              onClick={() => claim.mutate(detail.data!)}>
              {t("repackaging.actions.claim")}
            </Button>
          )}
          {detail.data?.status === "IN_PROGRESS" && canComplete && (
            <>
              <Button color="error" variant="outlined"
                startIcon={<WarningAmberOutlined />}
                onClick={() => {
                  setExceptionComment("");
                  setExceptionDialogOpen(true);
                }}>
                {t("repackaging.actions.reportProblem")}
              </Button>
              <Button variant="contained" color="success"
                startIcon={<CheckCircleOutline />}
                data-testid="repackaging-complete"
                disabled={!targetId || !departurePointId
                  || comment.trim().length < 3 || complete.isPending}
                onClick={() => complete.mutate({
                  task: detail.data!,
                  packagingId: targetId,
                  value: comment.trim()
                })}>
                {t("repackaging.actions.finish")}
              </Button>
            </>
          )}
          {detail.data?.status === "EXCEPTION" && canExecute && (
            <Button variant="contained"
              disabled={exceptionComment.trim().length < 3
                || resolveException.isPending}
              onClick={() => resolveException.mutate({
                task: detail.data!,
                action: resolutionAction,
                value: exceptionComment.trim()
              })}>
              {t("repackaging.actions.applyResolution")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={exceptionDialogOpen}
        onClose={() => setExceptionDialogOpen(false)}
        fullWidth maxWidth="sm">
        <DialogTitle>{t("repackaging.labels.reportDialog")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <FormControl fullWidth required>
              <InputLabel id="repack-exception-code-label">
                {t("repackaging.labels.reasonField")}
              </InputLabel>
              <Select labelId="repack-exception-code-label"
                label={t("repackaging.labels.reasonField")}
                value={exceptionCode}
                onChange={(event) => setExceptionCode(event.target.value)}>
                <MenuItem value="PACKAGING_UNAVAILABLE">
                  {t("repackaging.exceptions.PACKAGING_UNAVAILABLE")}
                </MenuItem>
                <MenuItem value="PACKAGING_DAMAGED">
                  {t("repackaging.exceptions.PACKAGING_DAMAGED")}
                </MenuItem>
                <MenuItem value="HU_DAMAGED">
                  {t("repackaging.exceptions.HU_DAMAGED")}
                </MenuItem>
                <MenuItem value="IDENTIFICATION_MISMATCH">
                  {t("repackaging.exceptions.IDENTIFICATION_MISMATCH")}
                </MenuItem>
                <MenuItem value="PROCESS_POINT_UNAVAILABLE">
                  {t("repackaging.exceptions.PROCESS_POINT_UNAVAILABLE")}
                </MenuItem>
                <MenuItem value="OTHER">{t("repackaging.exceptions.OTHER")}</MenuItem>
              </Select>
            </FormControl>
            <TextField label={t("repackaging.labels.comment")}
              value={exceptionComment}
              onChange={(event) => setExceptionComment(event.target.value)}
              multiline minRows={3} required
              helperText={t("repackaging.help.requiredThree")} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExceptionDialogOpen(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button color="error" variant="contained"
            disabled={exceptionComment.trim().length < 3
              || reportException.isPending || !detail.data}
            onClick={() => reportException.mutate({
              task: detail.data!,
              code: exceptionCode,
              value: exceptionComment.trim()
            })}>
            {t("repackaging.actions.report")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
